import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { VersionService, SolarData, SolarHistoryPoint, ThermometerReading, DayStat, TempDataPoint, WastePickup, UnifiSnapshot } from './version.service';
import { COMMIT_HASH } from '../version';
import { ColDef, GridOptions } from 'ag-grid-community';

export type LogLevel = 'all' | 'low' | 'medium' | 'high';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  readonly title          = 'Ebersgöns Dashboard';
  readonly frontendCommit = COMMIT_HASH;
  readonly currentYear    = new Date().getFullYear();

  solar:               SolarData | null                          = null;
  thermometers:        ThermometerReading[]                      = [];
  thermometerHistory:  {[deviceId: string]: TempDataPoint[]}     = {};

  solarHistory:          SolarHistoryPoint[]    = [];
  solarChartOpen         = false;

  solarFlash      = false;
  thermoFlash     = false;
  liveDotOn       = true;
  solarUpdatedAt: number | null   = null;
  thermoUpdatedAt: number | null  = null;
  nowTick         = Date.now();
  private _liveTimer: any;
  private wakeLock: any = null;
  isOnline = navigator.onLine;
  private readonly onOnline  = () => { this.isOnline = true; };
  private readonly onOffline = () => { this.isOnline = false; };
  private readonly onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      // bfcache restore: force a full fresh load with a cache-buster so the new bundle
      // is actually executed and bfcache cannot intercept the reload itself.
      location.replace(location.pathname + '?_t=' + Date.now() + location.hash);
    }
  };
  private readonly onVisibilityChange = async () => {
    if (document.visibilityState === 'visible') await this.requestWakeLock();
  };

  stats:               DayStat[]                                 = [];
  statsLoaded          = false;
  wasteLoaded          = false;
  unifi: UnifiSnapshot | null = null;
  unifiTimedOut = false;
  private static readonly VALID_TABS = ['energie','klima','muell','netzwerk','statistik'] as const;
  activeTab: 'energie' | 'klima' | 'muell' | 'statistik' | 'netzwerk' = (() => {
    const h = location.hash.replace('#','');
    return (AppComponent.VALID_TABS as readonly string[]).includes(h)
      ? h as any : 'energie';
  })();
  isMobileView = window.innerWidth < 768;

  setTab(tab: 'energie' | 'klima' | 'muell' | 'statistik' | 'netzwerk'): void {
    this.activeTab = tab;
    location.hash = tab;
  }

  @HostListener('window:resize')
  onResize() { this.isMobileView = window.innerWidth < 768; }

  wastePickups:        WastePickup[]                             = [];
  nextPickupsByType:   { type: string; name: string; date: string; icon: string }[] = [];
  logs:                any[]                                     = [];
  logLevel: LogLevel         = 'all';
  logsOpen  = false;
  darkMode  = false;

  colDefs: ColDef[] = [];

  readonly gridOptions: GridOptions = {
    animateRows: true,
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    onFirstDataRendered: (e) => {
      e.api.autoSizeColumns(['ts', 'level', 'version', 'className', 'stack']);
    },
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).then(() => {
        this.copiedCell = true;
        setTimeout(() => { this.copiedCell = false; }, 1200);
      }).catch(() => {});
    },
  };

  newVersionVisible        = false;
  versionCountdown         = 5;
  prefetchStatus: 'loading' | 'ready' | 'failed' = 'loading';
  prefetchLoaded           = 0;
  prefetchTotal            = 0;
  private _versionInterval: any = null;
  readonly location   = location;
  copiedCell          = false;
  expandedStackRows   = new Set<number>();
  langPickerOpen  = false;
  langSearch      = '';
  changelogOpen   = false;
  changelog: { hash: string; subject: string; date: string; author: string }[] = [];

  readonly languages = [
    { code: 'en', flag: '🇬🇧', name: 'English',    native: 'English' },
    { code: 'de', flag: '🇩🇪', name: 'German',     native: 'Deutsch' },
    { code: 'fr', flag: '🇫🇷', name: 'French',     native: 'Français' },
    { code: 'es', flag: '🇪🇸', name: 'Spanish',    native: 'Español' },
    { code: 'pt', flag: '🇵🇹', name: 'Portuguese', native: 'Português' },
    { code: 'it', flag: '🇮🇹', name: 'Italian',    native: 'Italiano' },
    { code: 'nl', flag: '🇳🇱', name: 'Dutch',      native: 'Nederlands' },
    { code: 'pl', flag: '🇵🇱', name: 'Polish',     native: 'Polski' },
    { code: 'ru', flag: '🇷🇺', name: 'Russian',    native: 'Русский' },
    { code: 'uk', flag: '🇺🇦', name: 'Ukrainian',  native: 'Українська' },
    { code: 'tr', flag: '🇹🇷', name: 'Turkish',    native: 'Türkçe' },
    { code: 'ar', flag: '🇸🇦', name: 'Arabic',     native: 'العربية' },
    { code: 'he', flag: '🇮🇱', name: 'Hebrew',     native: 'עברית' },
    { code: 'fa', flag: '🇮🇷', name: 'Persian',    native: 'فارسی' },
    { code: 'zh', flag: '🇨🇳', name: 'Chinese',    native: '中文' },
    { code: 'ja', flag: '🇯🇵', name: 'Japanese',   native: '日本語' },
    { code: 'ko', flag: '🇰🇷', name: 'Korean',     native: '한국어' },
    { code: 'hi', flag: '🇮🇳', name: 'Hindi',      native: 'हिन्दी' },
    { code: 'bn', flag: '🇧🇩', name: 'Bengali',    native: 'বাংলা' },
    { code: 'ur', flag: '🇵🇰', name: 'Urdu',       native: 'اردو' },
    { code: 'id', flag: '🇮🇩', name: 'Indonesian', native: 'Bahasa Indonesia' },
    { code: 'ms', flag: '🇲🇾', name: 'Malay',      native: 'Bahasa Melayu' },
    { code: 'th', flag: '🇹🇭', name: 'Thai',       native: 'ภาษาไทย' },
    { code: 'vi', flag: '🇻🇳', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'sv', flag: '🇸🇪', name: 'Swedish',    native: 'Svenska' },
    { code: 'no', flag: '🇳🇴', name: 'Norwegian',  native: 'Norsk' },
    { code: 'da', flag: '🇩🇰', name: 'Danish',     native: 'Dansk' },
    { code: 'fi', flag: '🇫🇮', name: 'Finnish',    native: 'Suomi' },
    { code: 'cs', flag: '🇨🇿', name: 'Czech',      native: 'Čeština' },
    { code: 'sk', flag: '🇸🇰', name: 'Slovak',     native: 'Slovenčina' },
    { code: 'hu', flag: '🇭🇺', name: 'Hungarian',  native: 'Magyar' },
    { code: 'ro', flag: '🇷🇴', name: 'Romanian',   native: 'Română' },
    { code: 'bg', flag: '🇧🇬', name: 'Bulgarian',  native: 'Български' },
    { code: 'hr', flag: '🇭🇷', name: 'Croatian',   native: 'Hrvatski' },
    { code: 'sr', flag: '🇷🇸', name: 'Serbian',    native: 'Српски' },
    { code: 'el', flag: '🇬🇷', name: 'Greek',      native: 'Ελληνικά' },
    { code: 'sw', flag: '🇰🇪', name: 'Swahili',    native: 'Kiswahili' },
    { code: 'am', flag: '🇪🇹', name: 'Amharic',    native: 'አማርኛ' },
    { code: 'af', flag: '🇿🇦', name: 'Afrikaans',  native: 'Afrikaans' },
    { code: 'ca', flag: '🇦🇩', name: 'Catalan',    native: 'Català' },
    { code: 'eu', flag: '🏴', name: 'Basque',      native: 'Euskara' },
    { code: 'gl', flag: '🇪🇸', name: 'Galician',   native: 'Galego' },
    { code: 'lt', flag: '🇱🇹', name: 'Lithuanian', native: 'Lietuvių' },
    { code: 'lv', flag: '🇱🇻', name: 'Latvian',    native: 'Latviešu' },
    { code: 'et', flag: '🇪🇪', name: 'Estonian',   native: 'Eesti' },
    { code: 'sl', flag: '🇸🇮', name: 'Slovenian',  native: 'Slovenščina' },
    { code: 'mk', flag: '🇲🇰', name: 'Macedonian', native: 'Македонски' },
    { code: 'sq', flag: '🇦🇱', name: 'Albanian',   native: 'Shqip' },
    { code: 'ka', flag: '🇬🇪', name: 'Georgian',   native: 'ქართული' },
    { code: 'hy', flag: '🇦🇲', name: 'Armenian',   native: 'Հայերեն' },
    { code: 'az', flag: '🇦🇿', name: 'Azerbaijani',native: 'Azərbaycan' },
    { code: 'kk', flag: '🇰🇿', name: 'Kazakh',     native: 'Қазақша' },
    { code: 'uz', flag: '🇺🇿', name: 'Uzbek',      native: 'Oʻzbekcha' },
    { code: 'mn', flag: '🇲🇳', name: 'Mongolian',  native: 'Монгол' },
    { code: 'ne', flag: '🇳🇵', name: 'Nepali',     native: 'नेपाली' },
    { code: 'si', flag: '🇱🇰', name: 'Sinhala',    native: 'සිංහල' },
    { code: 'my', flag: '🇲🇲', name: 'Burmese',    native: 'မြန်မာ' },
    { code: 'km', flag: '🇰🇭', name: 'Khmer',      native: 'ភាសាខ្មែរ' },
    { code: 'lo', flag: '🇱🇦', name: 'Lao',        native: 'ລາວ' },
    { code: 'ta', flag: '🇮🇳', name: 'Tamil',      native: 'தமிழ்' },
    { code: 'te', flag: '🇮🇳', name: 'Telugu',     native: 'తెలుగు' },
    { code: 'ml', flag: '🇮🇳', name: 'Malayalam',  native: 'മലയാളം' },
    { code: 'mr', flag: '🇮🇳', name: 'Marathi',    native: 'मराठी' },
    { code: 'gu', flag: '🇮🇳', name: 'Gujarati',   native: 'ગુજરાતી' },
    { code: 'pa', flag: '🇮🇳', name: 'Punjabi',    native: 'ਪੰਜਾਬੀ' },
    { code: 'ha', flag: '🇳🇬', name: 'Hausa',      native: 'Hausa' },
    { code: 'yo', flag: '🇳🇬', name: 'Yoruba',     native: 'Yorùbá' },
    { code: 'ig', flag: '🇳🇬', name: 'Igbo',       native: 'Igbo' },
    { code: 'zu', flag: '🇿🇦', name: 'Zulu',       native: 'IsiZulu' },
    { code: 'xh', flag: '🇿🇦', name: 'Xhosa',      native: 'IsiXhosa' },
    { code: 'so', flag: '🇸🇴', name: 'Somali',     native: 'Soomaali' },
    { code: 'ht', flag: '🇭🇹', name: 'Haitian Creole', native: 'Kreyòl Ayisyen' },
    { code: 'tl', flag: '🇵🇭', name: 'Filipino',   native: 'Filipino' },
    { code: 'jv', flag: '🇮🇩', name: 'Javanese',   native: 'Basa Jawa' },
    { code: 'cy', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', name: 'Welsh',       native: 'Cymraeg' },
    { code: 'ga', flag: '🇮🇪', name: 'Irish',      native: 'Gaeilge' },
    { code: 'is', flag: '🇮🇸', name: 'Icelandic',  native: 'Íslenska' },
    { code: 'lb', flag: '🇱🇺', name: 'Luxembourgish', native: 'Lëtzebuergesch' },
    { code: 'mt', flag: '🇲🇹', name: 'Maltese',    native: 'Malti' },
  ];

  readonly supportedLangs = new Set([
    'af','am','ar','az','bg','bn','ca','cs','cy','da','de','el','en','es','et',
    'eu','fa','fi','fr','ga','gl','gu','ha','he','hi','hr','ht','hu','hy','id',
    'ig','is','it','ja','jv','ka','kk','km','ko','lb','lo','lt','lv','mk','ml',
    'mn','mr','ms','mt','my','ne','nl','no','pa','pl','pt','ro','ru','si','sk',
    'sl','so','sq','sr','sv','sw','ta','te','th','tl','tr','uk','ur','uz','vi',
    'xh','yo','zh','zu'
  ]);

  get filteredLanguages() {
    const q = this.langSearch.toLowerCase();
    if (!q) return this.languages;
    return this.languages.filter(l =>
      l.name.toLowerCase().includes(q) || l.native.toLowerCase().includes(q)
    );
  }

  get currentLangFlag(): string {
    return this.languages.find(l => l.code === this.translate.currentLang)?.flag ?? '🌐';
  }

  logPassword      = '';
  logAuthError     = false;
  logAuthenticated = false;

  private subs: Subscription[] = [];

  constructor(
    readonly vs: VersionService,
    readonly translate: TranslateService
  ) {
    translate.addLangs(['en', 'de']);
    translate.setDefaultLang('en');

    this.darkMode = localStorage.getItem('darkMode') !== 'false';
    this.applyDarkMode();
  }

  toggleDarkMode(): void {
    this.darkMode = !this.darkMode;
    localStorage.setItem('darkMode', String(this.darkMode));
    this.applyDarkMode();
  }

  private applyDarkMode(): void {
    document.documentElement.setAttribute('data-bs-theme', this.darkMode ? 'dark' : 'light');
  }

  ngOnInit(): void {
    const saved = localStorage.getItem('lang') ?? 'en';
    this.translate.use(saved).subscribe(() => this.buildLogColDefs());

    // Rebuild log column headers when language changes
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.buildLogColDefs())
    );

    this._liveTimer = setInterval(() => { this.liveDotOn = !this.liveDotOn; }, 600);
    setInterval(() => { this.nowTick = Date.now(); }, 1000);

    this.vs.connect(this.frontendCommit);
    setTimeout(() => { if (this.unifi === null) this.unifiTimedOut = true; }, 10_000);

    this.subs.push(
      this.vs.solarHistory$.subscribe(pts => { this.solarHistory = pts; }),
      this.vs.solar$.subscribe(data => {
        this.solar = data;
        this.solarUpdatedAt = Date.now();
        const now = Date.now();
        const cutoff = now - 24 * 3600 * 1000;
        const pt: SolarHistoryPoint = { ts: now, pvYieldW: data.pvYieldW, batteryDischargeW: data.batteryDischargeW, gridConsumptionW: data.gridConsumptionW, totalConsumptionW: data.totalConsumptionW, batteryPercent: data.batteryPercent };
        this.solarHistory = [...this.solarHistory.filter(p => p.ts >= cutoff), pt];
        this.solarFlash = false;
        setTimeout(() => { this.solarFlash = true; setTimeout(() => { this.solarFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometers$.subscribe(data => {
        this.thermometers = data;
        this.thermoUpdatedAt = Date.now();
        this.thermoFlash = false;
        setTimeout(() => { this.thermoFlash = true; setTimeout(() => { this.thermoFlash = false; }, 900); }, 0);
      }),
      this.vs.thermometerHistory$.subscribe(h => { this.thermometerHistory = h; }),
      this.vs.stats$.subscribe(data => { this.stats = data; this.statsLoaded = true; }),
      this.vs.waste$.subscribe(data => {
        this.wastePickups = data;
        this.nextPickupsByType = this._computeNextPickups(data);
        this.wasteLoaded = true;
      }),
      this.vs.unifi$.subscribe(data => { this.unifi = data; this.unifiTimedOut = false; }),
      this.vs.newVersion$.subscribe(() => {
        if (this._versionInterval) return;
        this.newVersionVisible = true;
        this.versionCountdown  = 5;
        // Kick off asset prefetch; countdown only starts once prefetch settles (ready OR failed)
        this._prefetchUpdate().then(() => this._startCountdown());
      })
    );
    this.requestWakeLock();
    window.addEventListener('online',   this.onOnline);
    window.addEventListener('offline',  this.onOffline);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Download the new index.html + all its scripts into the browser cache. */
  private async _prefetchUpdate(): Promise<void> {
    this.prefetchStatus = 'loading';
    this.prefetchLoaded = 0;
    this.prefetchTotal  = 0;
    try {
      const html = await fetch('/', { cache: 'reload' }).then(r => r.text());
      const doc  = new DOMParser().parseFromString(html, 'text/html');
      const srcs = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]'))
        .map(s => s.getAttribute('src'))
        .filter((s): s is string => !!s && !s.startsWith('http'));
      this.prefetchTotal = srcs.length;
      await Promise.allSettled(
        srcs.map(src =>
          fetch(src, { cache: 'reload' })
            .then(() => { this.prefetchLoaded++; })
            .catch(() => { this.prefetchLoaded++; })
        )
      );
      this.prefetchStatus = 'ready';
    } catch {
      this.prefetchStatus = 'failed';
    }
  }

  private _startCountdown(): void {
    if (this._versionInterval) return;
    this._versionInterval = setInterval(() => {
      this.versionCountdown--;
      if (this.versionCountdown <= 0) {
        clearInterval(this._versionInterval);
        this._versionInterval = null;
        this.applyUpdate();
      }
    }, 1000);
  }

  /** Navigate to the new version. Uses a timestamp query param to prevent bfcache on mobile. */
  applyUpdate(): void {
    if (this._versionInterval) {
      clearInterval(this._versionInterval);
      this._versionInterval = null;
    }
    // Adding ?_t= forces a cache-miss URL, bypassing bfcache on iOS Safari.
    // The update overlay stays visible until the new page renders — no opacity flash.
    location.replace(location.pathname + '?_t=' + Date.now() + location.hash);
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    clearInterval(this._liveTimer);
    window.removeEventListener('online',   this.onOnline);
    window.removeEventListener('offline',  this.onOffline);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.wakeLock?.release?.();
  }

  private async requestWakeLock(): Promise<void> {
    if ('wakeLock' in navigator) {
      try { this.wakeLock = await (navigator as any).wakeLock.request('screen'); }
      catch (_) {}
    }
  }

  openChangelog(): void {
    this.changelogOpen = true;
    if (this.changelog.length === 0) {
      fetch('/api/changelog')
        .then(r => r.json())
        .then(data => { this.changelog = data; })
        .catch(() => {});
    }
  }

  openLangPicker(): void {
    this.langPickerOpen = true;
    this.langSearch = '';
  }

  selectLang(code: string): void {
    if (!this.supportedLangs.has(code)) return;
    this.langPickerOpen = false;
    this.switchLang(code);
  }

  switchLang(lang: string): void {
    localStorage.setItem('lang', lang);
    this.translate.use(lang);
  }

  private buildLogColDefs(): void {
    const t = (key: string) => this.translate.instant(key);

    this.colDefs = [
      {
        field: 'ts', headerName: t('COLS.ZEIT'), sortable: true,
        valueFormatter: p => this.fmtTs(p.value),
        sort: 'desc',
      },
      {
        field: 'level', headerName: t('COLS.LEVEL'), sortable: true, filter: true,
        cellRenderer: (p: any) => {
          const colour = this.levelBadge(p.value);
          return `<span class="badge bg-${colour}">${p.value ?? ''}</span>`;
        },
      },
      {
        field: 'version', headerName: t('COLS.VERSION'),
        valueFormatter: p => (p.value ?? '?').slice(0, 7),
      },
      { field: 'className', headerName: t('COLS.KLASSE'), sortable: true, filter: true },
      {
        field: 'message', headerName: t('COLS.NACHRICHT'), flex: 1, filter: true,
        wrapText: true, autoHeight: true,
      },
      {
        field: 'stack', headerName: t('COLS.STACK'), filter: true,
        autoHeight: true,
        cellRenderer: (p: any) => {
          const stack: string = p.value ?? '';
          if (!stack) return '<span class="text-muted">–</span>';
          const idx = p.rowIndex as number;
          const expanded = this.expandedStackRows.has(idx);
          const firstLine = stack.split('\n')[0];
          if (expanded) {
            return `<pre style="font-size:11px;line-height:1.4;margin:0;cursor:pointer;white-space:pre-wrap;word-break:break-all;" title="${this.translate.instant('STACK_EINKLAPPEN')}">▲ ${stack}</pre>`;
          }
          return `<span style="font-family:monospace;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;cursor:pointer;" title="${stack.replace(/"/g, '&quot;')}">▶ ${firstLine}</span>`;
        },
        onCellClicked: (p: any) => {
          const idx = p.rowIndex as number;
          if (this.expandedStackRows.has(idx)) {
            this.expandedStackRows.delete(idx);
          } else {
            this.expandedStackRows.add(idx);
          }
          p.api.redrawRows({ rowNodes: [p.node] });
        },
      },
    ];
  }

  fetchLogs(): void {
    const param = this.logLevel === 'all' ? '' : `?level=${this.logLevel}`;
    fetch(`/api/logs${param}`, { headers: { 'X-Log-Password': this.logPassword } })
      .then(r => {
        if (r.status === 401) {
          this.logAuthError     = true;
          this.logAuthenticated = false;
          this.logs = [];
          return null;
        }
        this.logAuthError     = false;
        this.logAuthenticated = true;
        return r.json();
      })
      .then(data => { if (data) this.logs = (data as any[]).reverse(); })
      .catch(() => {});
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
    this.fetchLogs();
  }

  toggleLogs(): void {
    this.logsOpen = !this.logsOpen;
    if (this.logsOpen && this.logAuthenticated) this.fetchLogs();
    if (!this.logsOpen) this.expandedStackRows.clear();
  }

  submitLogPassword(): void {
    this.fetchLogs();
  }

  fmtTs(ts: number | string): string {
    const d = new Date(Number(ts));
    const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
    return d.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'medium' });
  }

  levelBadge(level: string): string {
    return level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'secondary';
  }

  shortHash(hash: string): string {
    if (!hash || hash.length <= 9) return hash || '…';
    return hash.slice(0, 4) + '…' + hash.slice(-4);
  }

  get todayStat(): DayStat | null {
    const today = new Date().toISOString().slice(0, 10);
    return this.stats.find(s => s.date === today) ?? null;
  }

  private static readonly WASTE_ICONS: Record<string, string> = {
    RM:  'assets/waste/rm.svg',
    Bio: 'assets/waste/bio.svg',
    GT:  'assets/waste/gt.svg',
    PPK: 'assets/waste/ppk.svg',
    A:   'assets/waste/astwerk.svg',
    SoH: 'assets/waste/soh.svg',
    TB:  'assets/waste/tb.svg',
  };

  private _computeNextPickups(pickups: WastePickup[]): { type: string; name: string; date: string; icon: string }[] {
    const today = new Date().toISOString().slice(0, 10);
    const map   = new Map<string, { type: string; name: string; date: string; icon: string }>();
    for (const p of pickups) {
      if (p.type === 'SoG') continue;
      if (p.date < today)  continue;
      if (!map.has(p.type)) {
        map.set(p.type, {
          type: p.type,
          name: p.name,
          date: p.date,
          icon: AppComponent.WASTE_ICONS[p.type] ?? 'assets/waste/rm.svg',
        });
      }
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }
}
